import { L } from '@lib';
import { IDateRange } from '@models';
import { InputNumber, Space, Typography } from 'antd';
import React from 'react';
import { DateTimeRangeSelect } from '../Form/DateTimeRangeSelect';
import { OrderStatusSelect } from '../Select/OrderStatusSelect';
import { TechnicianSelect } from '../Select/TechnicianSelect';

const { Text } = Typography;

// Grey at 11px was barely there against the white panel. Heading ink at 12/700 reads as a label
// without competing with the values in the controls under it.
const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: 'rgba(0, 0, 0, 0.85)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  display: 'block',
  marginBottom: 4,
};

interface IProps {
  technicianId?: number;
  onTechnicianChange: (userId?: number) => void;

  /**
   * Omitted while the job is still a draft: there is nothing to have worked on yet, and no status
   * to move it through - a new job carries the one it was created with.
   */
  status?: string;
  onStatusChange?: (status: string) => void;
  workedTimeMinutes?: number;
  onWorkedTimeChange?: (minutes?: number) => void;

  /**
   * When this leg is booked in. On a build it is the one thing that orders the legs against each
   * other, so it belongs next to the status rather than only on the calendar.
   */
  dateRange?: IDateRange;
  onDateRangeChange?: (range: IDateRange) => void;

  /** A job that does not exist yet cannot be paid or ready to go. */
  hidePaid?: boolean;
  hideReady?: boolean;

  disabled?: boolean;
}

/**
 * The handful of job details worth having next to the services, on one line.
 *
 * Deliberately not JobDetailsCard: that carries the full job card - work station, mileage, logged
 * time, reminders, drop-off sync warnings - which is right on a page devoted to one job and far too
 * wide inside a build panel. A build is read by comparing its legs, so each leg shows only what
 * distinguishes it: who has it, how long it took, and where it has got to.
 */
export const JobDetailsRow: React.FC<IProps> = ({
  technicianId,
  onTechnicianChange,
  status,
  onStatusChange,
  workedTimeMinutes,
  onWorkedTimeChange,
  dateRange,
  onDateRangeChange,
  hidePaid = false,
  hideReady = false,
  disabled = false,
}) => (
  <Space size={16} wrap style={{ marginBottom: 12 }}>
    <div>
      <Text style={labelStyle}>{L('Technician')}</Text>
      <TechnicianSelect
        style={{ width: 200 }}
        disabled={disabled}
        value={technicianId}
        onSelect={onTechnicianChange}
        onClear={() => onTechnicianChange(undefined)}
      />
    </div>

    {onWorkedTimeChange && (
      <div>
        <Text style={labelStyle}>{L('Worked time')}</Text>
        <InputNumber
          min={0}
          controls={false}
          disabled={disabled}
          placeholder={L('Minutes')}
          style={{ width: 130 }}
          value={workedTimeMinutes}
          onChange={(value) => onWorkedTimeChange(value ?? undefined)}
        />
      </div>
    )}

    {onStatusChange && (
      <div>
        <Text style={labelStyle}>{L('Status')}</Text>
        <OrderStatusSelect
          hideFirstOption
          hidePaid={hidePaid}
          hideReady={hideReady}
          width={210}
          disabled={disabled}
          status={status}
          onChange={(value: string) => onStatusChange(value)}
        />
      </div>
    )}

    {onDateRangeChange && (
      <div style={{ minWidth: 320 }}>
        <Text style={labelStyle}>{L('Job date')}</Text>
        {/* The same three-part control the job card uses - date, start, end - rather than one
            picker with a time in it, so a leg is scheduled here exactly as it is there. */}
        <DateTimeRangeSelect
          disabled={disabled}
          dateRange={dateRange}
          setDateRange={onDateRangeChange}
        />
      </div>
    )}
  </Space>
);
